import { TooltipProvider } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { BriefTemplateIntakeSheet } from '@/features/brief-template-creation';
import { PageTransition } from '@/shared/ui/animations';
import { BreadcrumbPageHeader, ListPageBody, ListPageLayout } from '@/shared/ui/layout';
import { AboutPageDialog } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import { BriefTemplatesEmptyState } from './components/BriefTemplatesEmptyState';
import { BriefTemplatesErrorState } from './components/BriefTemplatesErrorState';
import { BriefTemplatesNoResultsState } from './components/BriefTemplatesNoResultsState';
import { BriefTemplatesTable } from './components/BriefTemplatesTable';
import { BriefTemplatesToolbar } from './components/BriefTemplatesToolbar';
import {
  briefTemplatesPageDescription,
  briefTemplatesPageTitle,
} from './constants/briefTemplates.constants';
import { useBriefTemplatesList } from './hooks/useBriefTemplatesList';

export function BriefTemplatesList() {
  const { t } = useTranslation('knowledge');
  const list = useBriefTemplatesList();
  const { handlers } = list;

  return (
    <TooltipProvider>
      <ListPageLayout>
        <PageTransition variant="slideUp" delay={0.0}>
          {
            <BreadcrumbPageHeader
              trail={[
                {
                  label: t('knowledgeHub.briefTemplatesHeader.knowledge'),
                  href: ROUTES.KNOWLEDGE_HUB,
                },
              ]}
              page={briefTemplatesPageTitle()}
              info={{ onClick: () => handlers.setAboutOpen(true) }}
            />
          }
        </PageTransition>

        <ListPageBody>
          <PageTransition variant="slideUp" delay={0.1} className="flex min-h-0 flex-1 flex-col">
            {list.showToolbar && (
              <BriefTemplatesToolbar
                search={list.search}
                onSearchChange={handlers.searchChange}
                onCreateClick={handlers.openCreate}
                canCreate={list.canCreate}
                filters={
                  list.levelsEnabled
                    ? {
                        level: list.levelFilter,
                        status: list.statusFilter,
                        onLevelChange: handlers.levelFilterChange,
                        onStatusChange: handlers.statusFilterChange,
                      }
                    : undefined
                }
              />
            )}
            {list.showError ? (
              <BriefTemplatesErrorState onRetry={handlers.retry} />
            ) : list.showInitialEmpty ? (
              <BriefTemplatesEmptyState onCreate={handlers.openCreate} canCreate={list.canCreate} />
            ) : list.showNoResults ? (
              <BriefTemplatesNoResultsState
                onClear={handlers.clearFilters}
                filtersActive={list.filtersActive}
              />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col overflow-auto px-section pb-section">
                <BriefTemplatesTable
                  templates={list.filteredTemplates}
                  isLoading={list.isLoading}
                  levelsEnabled={list.levelsEnabled}
                />
              </div>
            )}
          </PageTransition>
        </ListPageBody>
      </ListPageLayout>
      {/* Unmount on close so the internal form state resets. */}
      {list.createModalOpen && (
        <BriefTemplateIntakeSheet
          open
          onClose={handlers.closeCreate}
          onCreated={handlers.intakeCreated}
        />
      )}

      <AboutPageDialog
        open={list.isAboutOpen}
        onOpenChange={handlers.setAboutOpen}
        title={briefTemplatesPageTitle()}
        description={briefTemplatesPageDescription()}
        rows={[]}
      />
    </TooltipProvider>
  );
}
